/**
 * Cycle tracking, pure derivation of cycle phase from a last-period
 * start date. v1 uses a fixed 28-day average (customisable length is
 * a follow-up). Returns null when the input is missing or invalid so
 * the UI can degrade gracefully.
 *
 * Phases follow the clinical convention:
 *   - menstrual  (days 1-5)
 *   - follicular (days 6-13)
 *   - ovulatory  (days 14-16)
 *   - luteal     (days 17-28)
 *
 * `day` returned is 1-indexed within the current cycle (so day 1 is
 * the first day of the last period the user entered, day 29 rolls to
 * the next cycle's day 1).
 */

const CYCLE_LENGTH_DEFAULT = 28;
const PERIOD_LENGTH_DEFAULT = 5;

/** @param {string|null|undefined} iso */
function parseISODate(iso) {
  if (!iso || typeof iso !== 'string') return null;
  // Accept 'YYYY-MM-DD' and full ISO datetimes; we only care about
  // the calendar day in the user's local time.
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [, y, mo, d] = m;
  const t = new Date(Number(y), Number(mo) - 1, Number(d)).getTime();
  return Number.isFinite(t) ? t : null;
}

function startOfLocalDay(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Return { day, phase } for today given a last-period-start date, or
 * null if the input is missing / in the future / too far in the past
 * (>180 days, likely stale).
 *
 * @param {string} lastPeriodStart - ISO date string 'YYYY-MM-DD'
 * @param {Date} [today] - injectable for tests
 * @param {number} [cycleLength] - defaults to 28
 */
export function cyclePhase(
  lastPeriodStart,
  today = new Date(),
  cycleLength = CYCLE_LENGTH_DEFAULT,
) {
  const startT = parseISODate(lastPeriodStart);
  if (startT == null) return null;
  const todayT = startOfLocalDay(today);
  if (startT > todayT) return null; // future date
  const diffDays = Math.floor((todayT - startT) / 86400000);
  if (diffDays > 180) return null; // stale, ask the user to update
  const len = Math.max(21, Math.min(cycleLength, 45));
  const day = (diffDays % len) + 1;
  return { day, phase: phaseForDay(day, len), cycleLength: len };
}

/**
 * Phase for a 1-indexed cycle day. Boundaries scale with cycle
 * length per the project spec:
 *   Menstrual  = days 1..5
 *   Ovulatory  = day floor(length / 2)  (single day)
 *   Follicular = days 6..(ovulation - 1)
 *   Luteal     = days (ovulation + 1)..length
 *
 * For a 28-day cycle this gives menstrual 1-5, follicular 6-13,
 * ovulatory 14, luteal 15-28. For 21-day: menstrual 1-5, follicular
 * 6-9, ovulatory 10, luteal 11-21.
 *
 * @param {number} day - 1-indexed day in the cycle
 * @param {number} [cycleLength] - defaults to 28
 */
export function phaseForDay(day, cycleLength = CYCLE_LENGTH_DEFAULT) {
  const len = Math.max(21, Math.min(Number(cycleLength) || CYCLE_LENGTH_DEFAULT, 45));
  const ovulation = Math.floor(len / 2);
  if (day <= 5) return 'menstrual';
  if (day === ovulation) return 'ovulatory';
  if (day < ovulation) return 'follicular';
  return 'luteal';
}

/**
 * Walk a list of `{ entry_date, signals: { flow } }` rows and collapse
 * runs of consecutive bleed days into period start dates. A "start" is
 * any flow-positive day whose previous calendar day is not flow-positive.
 * Spotting counts as flow.
 *
 * @param {Array<{entry_date: string, signals?: {flow?: string|null}}>} entries
 * @returns {string[]} sorted ascending ISO 'YYYY-MM-DD' start dates
 */
export function findCycleStarts(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return [];
  const flowDays = new Set();
  for (const r of entries) {
    const flow = r?.signals?.flow;
    if (flow && flow !== 'none' && r.entry_date) {
      flowDays.add(r.entry_date);
    }
  }
  if (flowDays.size === 0) return [];
  const starts = [];
  for (const day of flowDays) {
    const prev = isoMinusOneDay(day);
    if (!flowDays.has(prev)) starts.push(day);
  }
  starts.sort();
  return starts;
}

function isoMinusOneDay(iso) {
  const t = parseISODate(iso);
  if (t == null) return '';
  const d = new Date(t - 86400000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/**
 * Pick the most recent start <= today from a list of starts. Falls back
 * to the user-entered `cycle_last_period_start` field when no diary
 * history exists yet (preserves backward compat with users who only
 * filled out the Profile field).
 *
 * @param {string[]} starts - from findCycleStarts(), ascending
 * @param {string|null} fallbackStart
 * @param {Date} [today]
 * @returns {string|null}
 */
export function mostRecentPeriodStart(starts, fallbackStart = null, today = new Date(), userProgress = null) {
  const todayT = startOfLocalDay(today);
  // Merge the diary-derived starts with any manual history the user
  // entered at onboarding / Profile, so a new user with no logged
  // bleed days but a remembered period date still anchors her arc.
  const merged = mergeCycleStarts(starts, userProgress?.cycle_history_starts);
  let best = null;
  for (const s of merged) {
    const t = parseISODate(s);
    if (t == null) continue;
    if (t > todayT) continue;
    if (best == null || t > best.t) best = { iso: s, t };
  }
  if (best) return best.iso;
  const fbT = parseISODate(fallbackStart);
  if (fbT != null && fbT <= todayT) return fallbackStart;
  return null;
}

/**
 * Average of the gaps between consecutive cycle starts. Used to refine
 * cycle-length predictions once we have at least two starts logged.
 * Returns null when there's not enough data; clamped to [21, 40] to
 * stay in the same range cyclePhase() supports.
 *
 * @param {string[]} starts - sorted ascending
 * @returns {number|null}
 */
export function averageCycleLength(starts) {
  if (!Array.isArray(starts) || starts.length < 2) return null;
  let sum = 0;
  let n = 0;
  for (let i = 1; i < starts.length; i++) {
    const a = parseISODate(starts[i - 1]);
    const b = parseISODate(starts[i]);
    if (a == null || b == null) continue;
    const days = Math.round((b - a) / 86400000);
    if (days >= 14 && days <= 60) {
      sum += days;
      n += 1;
    }
  }
  if (n === 0) return null;
  const avg = Math.round(sum / n);
  return Math.max(21, Math.min(avg, 45));
}

/**
 * Brand-aligned colour token for each cycle phase. Maps to the
 * movement palette: menstrual = release/coral, follicular = relax/teal,
 * ovulatory = squeeze/magenta (the brand primary), luteal = soft push.
 * Returns CSS variable references so light/dark theme stays honest.
 */
export function phaseColor(phase) {
  switch (phase) {
    case 'menstrual':  return 'var(--push, #E5736B)';
    case 'follicular': return 'var(--relax, #5FAA9F)';
    case 'ovulatory':  return 'var(--primary, #A8003F)';
    case 'luteal':     return 'var(--warm-brown, #8B7355)';
    default:           return 'var(--warm-brown, #8B7355)';
  }
}

/** Lighter tinted version of phaseColor for cell backgrounds. */
export function phaseTint(phase) {
  const c = phaseColor(phase);
  return `color-mix(in srgb, ${c} 14%, transparent)`;
}

/** Clamp a cycle length into the supported range [21, 45]. */
export function clampCycleLength(n) {
  // Reject null/undefined/empty before Number() coerces them to 0,
  // which would otherwise clamp to the lower bound (21) instead of
  // honoring the default.
  if (n === null || n === undefined || n === '') return CYCLE_LENGTH_DEFAULT;
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return CYCLE_LENGTH_DEFAULT;
  return Math.max(21, Math.min(v, 45));
}

/** Clamp a period (bleed) length into the supported range [2, 10]. */
export function clampPeriodLength(n) {
  if (n === null || n === undefined || n === '') return PERIOD_LENGTH_DEFAULT;
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return PERIOD_LENGTH_DEFAULT;
  return Math.max(2, Math.min(v, 10));
}

/**
 * Merge two arrays of ISO date strings (e.g., diary-derived cycle starts
 * + manually-entered history) into a single sorted, de-duped, validated
 * list. Invalid entries are dropped. Output is ascending.
 */
export function mergeCycleStarts(a, b) {
  const set = new Set();
  const arrays = [Array.isArray(a) ? a : [], Array.isArray(b) ? b : []];
  for (const arr of arrays) {
    for (const iso of arr) {
      if (parseISODate(iso) != null) set.add(iso);
    }
  }
  return [...set].sort();
}

/**
 * Single resolver for "how long is this user's cycle, really?" Used by
 * every consumer (CycleArc, Calendar, WeekHome, BodyLetter, PhaseDetail)
 * so phase calculations agree across the app. Precedence:
 *
 *   1. Logged history (diary-derived bleed starts + any manual history
 *      the user entered at onboarding / Profile) wins once we have >= 3
 *      total starts. Per Hendrickson-Jack (*The Fifth Vital Sign*),
 *      observed bleed gaps are the truth; a self-reported "I think my
 *      cycle is 30 days" is an estimate that should defer to evidence
 *      once enough exists.
 *   2. Otherwise, honour the user's explicit cycle_length setting.
 *   3. Otherwise, fall back to 28.
 *
 * The 3-start threshold avoids over-reacting to a single short or long
 * cycle — every body has stress months — while still letting the system
 * learn within a few cycles. Manual `cycle_history_starts` entered at
 * onboarding / Profile are treated as first-class so a brand-new user
 * who has never opened the diary still gets her own cycle length on
 * day 1, not the population default.
 */
export function resolveCycleLength(userProgress, cycleStarts) {
  const merged = mergeCycleStarts(cycleStarts, userProgress?.cycle_history_starts);
  if (merged.length >= 3) {
    const derived = averageCycleLength(merged);
    if (derived) return derived;
  }
  const stored = userProgress?.cycle_length;
  if (stored) return clampCycleLength(stored);
  return CYCLE_LENGTH_DEFAULT;
}

/**
 * Period (bleed) length resolver. Walks `entries` to count consecutive
 * flow days per cycle, averages across cycles, and clamps. Falls back
 * to the user's stored cycle_period_length, then to 5.
 *
 * @param {object} userProgress
 * @param {Array<{entry_date: string, signals?: {flow?: string|null}}>} entries
 * @returns {number}
 */
export function resolvePeriodLength(userProgress, entries) {
  const derived = averagePeriodLength(entries);
  if (derived) return derived;
  return clampPeriodLength(userProgress?.cycle_period_length);
}

/**
 * Average length of bleed runs across the provided diary entries.
 * A "bleed run" is a maximal set of consecutive calendar days with
 * flow !== 'none'. Spotting counts as flow (mirrors findCycleStarts).
 * Returns null when there's no flow data; clamped to [2, 10].
 */
export function averagePeriodLength(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  const flowDays = new Set();
  for (const r of entries) {
    const flow = r?.signals?.flow;
    if (flow && flow !== 'none' && r.entry_date) flowDays.add(r.entry_date);
  }
  if (flowDays.size === 0) return null;
  const runs = [];
  for (const day of flowDays) {
    const prev = isoMinusOneDay(day);
    if (!flowDays.has(prev)) {
      let cursor = day;
      let len = 1;
      while (flowDays.has(isoPlusOneDay(cursor))) {
        cursor = isoPlusOneDay(cursor);
        len += 1;
      }
      runs.push(len);
    }
  }
  if (runs.length === 0) return null;
  const sum = runs.reduce((a, b) => a + b, 0);
  return clampPeriodLength(Math.round(sum / runs.length));
}

function isoPlusOneDay(iso) {
  const t = parseISODate(iso);
  if (t == null) return '';
  return isoFromMs(t + 86400000);
}

/**
 * Predicted ovulation day for a cycle. Returns the ISO calendar date
 * of the day the user is most likely ovulating.
 *
 * Formula: ovulation occurs at cycle day floor(length / 2). For a
 * 28-day cycle that is day 14, for a 30-day cycle day 15, for a
 * 21-day cycle day 10. This is the simplest, most-defensible single
 * value to surface; we widen it into a 6-day window via the fertile
 * helper below to absorb biological variation.
 */
export function ovulationDate(startIso, cycleLength = CYCLE_LENGTH_DEFAULT) {
  const startT = parseISODate(startIso);
  if (startT == null) return null;
  const len = clampCycleLength(cycleLength);
  const ovOffset = Math.floor(len / 2); // cycle day, 1-indexed
  // Day 1 = startT (offset 0). Day N = startT + (N - 1) days.
  return isoFromMs(startT + (ovOffset - 1) * 86400000);
}

/**
 * Forecast next-period start dates for up to 3 cycles ahead. Returns
 * an array, ordered oldest-to-newest, where each entry has the start
 * date and a typical 5-day bleed window. A cycle is included only if
 * its predicted start is in the future relative to `today`.
 *
 * @param {string} startIso - most recent confirmed period start
 * @param {number} cycleLength
 * @param {Date} [today]
 * @param {number} [aheadCount] - how many cycles ahead to project
 * @returns {Array<{ start: string, days: string[] }>}
 */
export function forecastNextPeriods(
  startIso,
  cycleLength = CYCLE_LENGTH_DEFAULT,
  today = new Date(),
  aheadCount = 3,
  periodLength = PERIOD_LENGTH_DEFAULT,
) {
  const startT = parseISODate(startIso);
  if (startT == null) return [];
  const len = clampCycleLength(cycleLength);
  const bleed = clampPeriodLength(periodLength);
  const todayT = startOfLocalDay(today);
  const out = [];
  // Find the first predicted start strictly after today, then walk
  // forward `aheadCount` cycles from there.
  let cursor = startT + len * 86400000;
  while (cursor <= todayT) cursor += len * 86400000;
  for (let c = 0; c < aheadCount; c++) {
    const dayMs = cursor + c * len * 86400000;
    const days = [];
    for (let i = 0; i < bleed; i++) days.push(isoFromMs(dayMs + i * 86400000));
    out.push({ start: isoFromMs(dayMs), days });
  }
  return out;
}

/**
 * Single-cycle convenience around forecastNextPeriods, kept so call
 * sites that only need the next period don't have to pluck out [0].
 */
export function forecastNextPeriod(
  startIso,
  cycleLength = CYCLE_LENGTH_DEFAULT,
  today = new Date(),
  periodLength = PERIOD_LENGTH_DEFAULT,
) {
  const all = forecastNextPeriods(startIso, cycleLength, today, 1, periodLength);
  return all[0] || null;
}

/**
 * Bleed-window for the CURRENT cycle (the one that began on
 * `startIso`). Used to paint expected period days on the calendar
 * even when the user hasn't logged daily flow yet — e.g., she just
 * told us "today is day 1" and we should now show today + the next
 * `periodLength - 1` days as expected. The user can always override
 * any single day via PeriodQuickSheet (long-press on the cell).
 *
 * Returns [] when the current cycle is already past its bleed window
 * (i.e., today is more than `periodLength` days after start) so the
 * UI doesn't repaint days the user has already lived through.
 */
export function currentPeriodWindow(
  startIso,
  periodLength = PERIOD_LENGTH_DEFAULT,
  today = new Date(),
) {
  const startT = parseISODate(startIso);
  if (startT == null) return [];
  const bleed = clampPeriodLength(periodLength);
  const todayT = startOfLocalDay(today);
  // Only paint the window while today is still within it. After that
  // the window becomes "history" and is the user's flow log to own.
  const ageDays = Math.floor((todayT - startT) / 86400000);
  if (ageDays < 0 || ageDays >= bleed) return [];
  const out = [];
  for (let i = 0; i < bleed; i++) out.push(isoFromMs(startT + i * 86400000));
  return out;
}

/**
 * Fertile window: the 6 days during which conception is most likely,
 * comprising the five days before predicted ovulation plus ovulation
 * itself (the medically standard window per WHO). Always returns the
 * NEXT UPCOMING window relative to `today`, rolling forward through
 * future cycles when the current cycle's window has already passed.
 * Past days inside the active window are filtered out so today + the
 * remaining future days are what the UI shows.
 *
 * @param {string} startIso - cycle start
 * @param {number} cycleLength
 * @param {Date} [today]
 * @returns {string[]} ISO dates in the upcoming fertile window
 */
export function forecastFertileWindow(startIso, cycleLength = CYCLE_LENGTH_DEFAULT, today = new Date()) {
  const startT = parseISODate(startIso);
  if (startT == null) return [];
  const len = clampCycleLength(cycleLength);
  const todayT = startOfLocalDay(today);
  const ovOffset = Math.floor(len / 2); // 1-indexed cycle day
  // Roll forward through cycles until we land on one whose ovulation
  // day is today or later. Cap at 12 cycles forward as a runaway guard
  // (a year of cycles is more than enough for any realistic UI need).
  let cycleStartT = startT;
  let ovDayMs = cycleStartT + (ovOffset - 1) * 86400000;
  let safety = 0;
  while (ovDayMs < todayT && safety < 12) {
    cycleStartT += len * 86400000;
    ovDayMs = cycleStartT + (ovOffset - 1) * 86400000;
    safety += 1;
  }
  const window = [];
  for (let i = -5; i <= 0; i++) {
    const dayMs = ovDayMs + i * 86400000;
    if (dayMs < todayT) continue;
    window.push(isoFromMs(dayMs));
  }
  return window;
}

/**
 * Predicted ovulation day for the active or next-upcoming cycle.
 * Rolls forward through cycles like forecastFertileWindow so the UI
 * always has an ovulation accent to render once the user has set up
 * cycle data. Returns the ISO date or null on invalid input.
 */
export function ovulationDateIfFuture(startIso, cycleLength = CYCLE_LENGTH_DEFAULT, today = new Date()) {
  const startT = parseISODate(startIso);
  if (startT == null) return null;
  const len = clampCycleLength(cycleLength);
  const todayT = startOfLocalDay(today);
  const ovOffset = Math.floor(len / 2);
  let cycleStartT = startT;
  let ovDayMs = cycleStartT + (ovOffset - 1) * 86400000;
  let safety = 0;
  while (ovDayMs < todayT && safety < 12) {
    cycleStartT += len * 86400000;
    ovDayMs = cycleStartT + (ovOffset - 1) * 86400000;
    safety += 1;
  }
  return isoFromMs(ovDayMs);
}

function isoFromMs(ms) {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/**
 * End-of-cycle body letter analyzer. When today (or recently) marks
 * the start of a new cycle, returns a structured summary of the
 * cycle that just closed: length in days, plus the cycle days when
 * the user's pelvic floor felt heaviest, strongest, and quietest.
 *
 * Returns null when:
 *  - today is not within `recencyDays` of a cycle start (so the
 *    letter only fires once per cycle, as the new bleed begins)
 *  - there is no previous cycle to summarize
 *  - the previous cycle has no logged floor_feel data to draw on
 *
 * The letter is the moment of recognition that closes the loop on
 * everything we collect: flow days, floor sensations, symptoms.
 * Without this, the data sits passive; with it, every cycle ends
 * with the user being told what her body said.
 *
 * @param {Array<{entry_date: string, signals?: object}>} entries
 * @param {Date} [today]
 * @param {number} [recencyDays] - days after a new period start during which the letter is "fresh" (default 3)
 * @returns {{
 *   cycleStart: string, cycleEnd: string, lengthDays: number,
 *   heaviestRange: {start: number, end: number}|null,
 *   strongestDay: number|null,
 *   quietestDay: number|null,
 *   feelCount: number,
 * }|null}
 */
export function endOfCycleSummary(entries, today = new Date(), recencyDays = 3) {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  const starts = findCycleStarts(entries);
  if (starts.length < 2) return null;

  // Find the most recent start whose age is within the recency window.
  // We don't fire on the first day of bleeding only, the user often
  // doesn't open the app on day 1; a 3-day window catches the catch-up.
  const todayMs = startOfLocalDay(today);
  let triggerStart = null;
  for (let i = starts.length - 1; i >= 0; i--) {
    const t = parseISODate(starts[i]);
    if (t == null) continue;
    const ageDays = Math.floor((todayMs - t) / 86400000);
    if (ageDays >= 0 && ageDays < recencyDays) { triggerStart = starts[i]; break; }
    if (ageDays >= recencyDays) break;
  }
  if (!triggerStart) return null;

  // Find the cycle that ended when triggerStart began.
  const idx = starts.indexOf(triggerStart);
  if (idx < 1) return null;
  const prevStart = starts[idx - 1];
  const prevStartMs = parseISODate(prevStart);
  const triggerStartMs = parseISODate(triggerStart);
  if (prevStartMs == null || triggerStartMs == null) return null;
  const lengthDays = Math.round((triggerStartMs - prevStartMs) / 86400000);
  if (lengthDays < 14 || lengthDays > 60) return null;

  // Walk floor_feel entries inside the closed cycle. Translate dates
  // into 1-indexed cycle days so the letter reads as "day 14"
  // regardless of when the user actually bled.
  const feelByDay = new Map();
  for (const r of entries) {
    const feel = r?.signals?.floor_feel;
    if (!feel) continue;
    const t = parseISODate(r.entry_date);
    if (t == null) continue;
    if (t < prevStartMs || t >= triggerStartMs) continue;
    const day = Math.floor((t - prevStartMs) / 86400000) + 1;
    feelByDay.set(day, feel);
  }
  if (feelByDay.size === 0) return null;

  // Find heaviest run (consecutive 'heavy' days), strongest single
  // day, quietest single day. Falling back to the first/last logged
  // feel when there's only one to talk about.
  let heaviestRange = null;
  let runStart = null;
  let strongestDay = null;
  let quietestDay = null;
  const sortedDays = [...feelByDay.keys()].sort((a, b) => a - b);
  for (let i = 0; i < sortedDays.length; i++) {
    const d = sortedDays[i];
    const f = feelByDay.get(d);
    if (f === 'heavy') {
      if (runStart == null) runStart = d;
      const next = sortedDays[i + 1];
      const isLast = i === sortedDays.length - 1;
      const breaksRun = isLast || feelByDay.get(next) !== 'heavy' || next !== d + 1;
      if (breaksRun) {
        const span = d - runStart + 1;
        const prevSpan = heaviestRange ? heaviestRange.end - heaviestRange.start + 1 : 0;
        if (span > prevSpan) heaviestRange = { start: runStart, end: d };
        runStart = null;
      }
    }
    if (f === 'strong' && strongestDay == null) strongestDay = d;
    if (f === 'quiet' && quietestDay == null) quietestDay = d;
  }

  return {
    cycleStart: prevStart,
    cycleEnd: triggerStart,
    lengthDays,
    heaviestRange,
    strongestDay,
    quietestDay,
    feelCount: feelByDay.size,
  };
}
